import { useTranslation } from "@/common/providers/i18n-provider";
import type {
  OverlapComparison,
  OverlapProposal,
} from "@/lib/csv-overlap";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { translatedCsvReason } from "./csv-reason-i18n";

/** Cap the review table so a full-year overlap cannot flood the DOM. */
const MAX_OVERLAP_REVIEW = 50;

interface CsvOverlapReviewProps {
  priorText: string;
  onPriorChange: (value: string) => void;
  comparison: OverlapComparison | null;
  excludedRowIndexes: ReadonlySet<number>;
  onExcludedChange: (next: Set<number>) => void;
}

function statusMessage(
  t: (key: string, substitutions?: Record<string, string>) => string,
  comparison: OverlapComparison,
  locale: string,
): string {
  if (comparison.status === "empty") {
    return t("tools.csvWidget.overlap.status.empty");
  }
  if (comparison.status === "incompatible") {
    switch (comparison.statusReason) {
      case "incompatible_shape":
        return t("tools.csvWidget.overlap.status.incompatibleShape");
      case "missing_required_columns":
        return t("tools.csvWidget.overlap.status.missingColumns");
      case "blocking_config":
        return t("tools.csvWidget.overlap.status.blockingConfig");
      case "incompatible_headers":
      default:
        return t("tools.csvWidget.overlap.status.incompatibleHeaders");
    }
  }
  return t("tools.csvWidget.overlap.status.ready", {
    count: comparison.proposals.length.toLocaleString(locale),
  });
}

function ProposalRow({
  proposal,
  excluded,
  onToggle,
}: {
  proposal: OverlapProposal;
  excluded: boolean;
  onToggle: (exclude: boolean) => void;
}) {
  const { t, locale } = useTranslation();
  const controlId = `csv-overlap-row-${proposal.currentRowIndex}`;
  return (
    <tr>
      <td>
        {t("tools.csvWidget.overlap.review.line", {
          line: proposal.currentLine.toLocaleString(locale),
        })}
      </td>
      <td dir="ltr">{proposal.date}</td>
      <td dir="auto">{proposal.description}</td>
      <td dir="ltr">
        {proposal.amountText} {proposal.currency}
      </td>
      <td>
        {t("tools.csvWidget.overlap.review.priorLine", {
          line: proposal.priorLine.toLocaleString(locale),
        })}
      </td>
      <td>
        <div
          role="group"
          aria-labelledby={controlId}
          className="tool-overlap-controls"
        >
          <span id={controlId} className="sr-only">
            {t("tools.csvWidget.overlap.review.line", {
              line: proposal.currentLine.toLocaleString(locale),
            })}
          </span>
          <label className="tool-checkbox">
            <input
              type="radio"
              name={controlId}
              checked={!excluded}
              onChange={() => onToggle(false)}
            />
            <span className="tool-label-text">
              {t("tools.csvWidget.overlap.review.keep")}
            </span>
          </label>
          <label className="tool-checkbox">
            <input
              type="radio"
              name={controlId}
              checked={excluded}
              onChange={() => onToggle(true)}
            />
            <span className="tool-label-text">
              {t("tools.csvWidget.overlap.review.exclude")}
            </span>
          </label>
        </div>
      </td>
    </tr>
  );
}

/**
 * Optional prior-export input and occurrence review. Keeps prior CSV text in
 * React state only; never writes it to profiles or analytics.
 */
export function CsvOverlapReview({
  priorText,
  onPriorChange,
  comparison,
  excludedRowIndexes,
  onExcludedChange,
}: CsvOverlapReviewProps) {
  const { t, locale } = useTranslation();
  const [readStatus, setReadStatus] = useState<"idle" | "reading" | "failed">(
    "idle",
  );
  const readVersion = useRef(0);

  function clearPrior() {
    readVersion.current += 1;
    setReadStatus("idle");
    onPriorChange("");
  }

  async function readFile(file: File) {
    const version = ++readVersion.current;
    setReadStatus("reading");
    try {
      const contents = await file.text();
      if (readVersion.current !== version) return;
      onPriorChange(contents);
      setReadStatus("idle");
    } catch {
      if (readVersion.current === version) setReadStatus("failed");
    }
  }

  function setExcluded(rowIndex: number, exclude: boolean) {
    const next = new Set(excludedRowIndexes);
    if (exclude) next.add(rowIndex);
    else next.delete(rowIndex);
    onExcludedChange(next);
  }

  const proposals = comparison?.status === "ready" ? comparison.proposals : [];
  const visible = proposals.slice(0, MAX_OVERLAP_REVIEW);
  const hidden = proposals.length - visible.length;
  const priorIssues =
    comparison?.issues.filter((issue) => issue.side === "prior") ?? [];

  return (
    <section className="tool-overlap" aria-labelledby="csv-overlap-heading">
      <h2 id="csv-overlap-heading" className="tool-settings-heading">
        {t("tools.csvWidget.overlap.title")}
      </h2>
      <p className="tool-local-note">{t("tools.csvWidget.overlap.description")}</p>

      <div className="tool-panel-heading">
        <label htmlFor="csv-prior-input">
          {t("tools.csvWidget.overlap.input.label")}
        </label>
        <div className="tool-file-actions">
          {priorText.trim() !== "" ? (
            <button type="button" onClick={clearPrior}>
              {t("tools.csvWidget.overlap.clear")}
            </button>
          ) : null}
          <label className="tool-upload-action">
            <Upload size={17} aria-hidden="true" />
            {t("tools.csvWidget.overlap.input.upload")}
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void readFile(file);
              }}
            />
          </label>
        </div>
      </div>
      <textarea
        id="csv-prior-input"
        value={priorText}
        onChange={(event) => {
          readVersion.current += 1;
          setReadStatus("idle");
          onPriorChange(event.target.value);
        }}
        spellCheck={false}
        rows={6}
        dir="ltr"
        placeholder={t("tools.csvWidget.overlap.input.placeholder")}
        className="tool-source"
      />
      {readStatus === "reading" ? (
        <p className="tool-status" role="status">
          {t("tools.input.reading")}
        </p>
      ) : null}
      {readStatus === "failed" ? (
        <p className="tool-notice" role="alert">
          {t("tools.input.readError")}
        </p>
      ) : null}

      {comparison !== null && priorText.trim() !== "" ? (
        <p className="tool-status" role="status">
          {statusMessage(t, comparison, locale)}
        </p>
      ) : null}

      {visible.length > 0 ? (
        <>
          <h3 className="tool-notes-heading">
            {t("tools.csvWidget.overlap.review.title")}
          </h3>
          <p className="tool-local-note">
            {t("tools.csvWidget.overlap.review.hint")}
          </p>
          <div className="tool-overlap-table-wrap">
            <table className="tool-overlap-table">
              <thead>
                <tr>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colLine")}
                  </th>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colDate")}
                  </th>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colDescription")}
                  </th>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colAmount")}
                  </th>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colPrior")}
                  </th>
                  <th scope="col">
                    {t("tools.csvWidget.overlap.review.colAction")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((proposal) => (
                  <ProposalRow
                    key={proposal.currentRowIndex}
                    proposal={proposal}
                    excluded={excludedRowIndexes.has(proposal.currentRowIndex)}
                    onToggle={(exclude) =>
                      setExcluded(proposal.currentRowIndex, exclude)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hidden > 0 ? (
            <p className="tool-local-note">
              {t("tools.csvWidget.overlap.review.more", {
                count: hidden.toLocaleString(locale),
              })}
            </p>
          ) : null}
        </>
      ) : null}

      {priorIssues.length > 0 ? (
        <div className="tool-notes">
          <h3 className="tool-notes-heading">
            {t("tools.csvWidget.overlap.issues.title")}
          </h3>
          <ul className="tool-notes-list">
            {priorIssues.slice(0, 12).map((issue) => (
              <li key={`prior-${issue.line}-${issue.reason}`}>
                {t("tools.csvWidget.errors.line", {
                  line: issue.line.toLocaleString(locale),
                  reason: translatedCsvReason(t, issue.reason),
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
